/**
 * Module 00 — Global Shell · Credits meter hydration (FE-00-09)
 *
 * Fetches the org credits summary from the data-service and writes `{used,
 * total}` into the scope store, which drives the sidebar footer meter. Uses the
 * TanStack `staleTime: 60s` convention and the `['credits','meter', orgId]`
 * query key. Degrades silently — a failure leaves the meter at its neutral
 * state rather than blocking the shell.
 */

'use client';

import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { creditsService } from '@/lib/api/services/credits';
import { useScopeStore } from '@/lib/stores/scope-store';

export function useCredits() {
  const orgId = useScopeStore((s) => s.org?.id);
  const setCredits = useScopeStore((s) => s.setCredits);

  const query = useQuery({
    queryKey: ['credits', 'meter', orgId],
    queryFn: () => creditsService.getSummary(orgId as string),
    enabled: !!orgId,
    staleTime: 60_000,
    retry: false,
    throwOnError: false,
  });

  useEffect(() => {
    if (query.data) {
      setCredits({ used: query.data.used, total: query.data.total });
    }
  }, [query.data, setCredits]);

  return query;
}
